package org.teddapp.project.ui.projects

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.tedd.db.ProjectStatus
import com.tedd.db.Task
import org.teddapp.project.db.nowMillis
import org.teddapp.project.ui.InkButton
import org.teddapp.project.ui.TeddDates
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddInkBlock
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.TeddType
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Spacer
import androidx.compose.runtime.remember
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.height

/** Une tâche est à l'arrêt si rien ne l'a touchée depuis ce nombre de jours. */
internal const val STALE_DAYS = 7

/**
 * Santé d'un projet, calculée et non déclarée : ce qui est ouvert, ce qui est en retard,
 * ce qui a été terminé cette semaine, et la prochaine action à faire.
 */
internal data class ProjectHealth(
    val open: Int,
    val overdue: Int,
    val doneThisWeek: Int,
    val nextAction: Task?,
    val stalest: Long,
) {
    /** Vrai quand rien n'a bougé depuis une semaine et qu'il reste du travail. */
    val stalled: Boolean get() = open > 0 && stalest >= STALE_DAYS
}

/** Jours écoulés depuis la dernière modification d'une tâche. */
internal fun daysSince(updatedAt: Long, now: Long = nowMillis()): Long =
    ((now - updatedAt) / 86_400_000L).coerceAtLeast(0L)

internal fun computeProjectHealth(tasks: List<Task>, statuses: List<ProjectStatus>): ProjectHealth {
    val terminalIds = statuses.filter { it.is_terminal == 1L }.map { it.id }.toHashSet()
    val live = tasks.filter { it.is_deleted == 0L }
    val open = live.filter { it.status != "done" && it.status_id !in terminalIds }
    val today = TeddDates.todayIso
    val weekAgo = TeddDates.addDays(today, -7)
    val now = nowMillis()
    return ProjectHealth(
        open = open.size,
        overdue = open.count { !it.due_date.isNullOrBlank() && it.due_date!! < today },
        doneThisWeek = live.count { (it.status == "done" || it.status_id in terminalIds) && daysSince(it.updated_at, now) <= 7 },
        // La prochaine action : la plus urgente des tâches ouvertes, échéance d'abord.
        nextAction = open.sortedWith(
            compareBy<Task> { it.due_date ?: "9999-99-99" }
                .thenBy { if (it.priority == "high") 0 else if (it.priority == "medium") 1 else 2 }
                .thenBy { it.order_index },
        ).firstOrNull(),
        stalest = open.minOfOrNull { daysSince(it.updated_at, now) } ?: 0L,
    )
}

/**
 * Tête d'un projet sur téléphone, en une carte : l'avancement, ce qui demande attention,
 * puis la prochaine action. On sait quoi faire sans lire le tableau.
 */
@Composable
internal fun ProjectHealthStrip(
    health: ProjectHealth,
    onOpenTask: (String) -> Unit,
    onSendToFocus: ((Task) -> Unit)?,
    horizontalPadding: Dp,
    done: Int = 0,
    total: Int = 0,
) {
    val shape = RoundedCornerShape(TeddTokens.RMd)
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = horizontalPadding)
            .background(TeddTokens.Surface, shape)
            .border(1.dp, TeddTokens.Line, shape),
    ) {
        Column(modifier = Modifier.padding(14.dp), verticalArrangement = Arrangement.spacedBy(10.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                org.teddapp.project.ui.TeddFineProgress(
                    fraction = if (total > 0) done.toFloat() / total else 0f,
                    modifier = Modifier.weight(1f),
                    height = 6.dp,
                )
                Text(
                    text = if (total == 0) "Aucune tâche" else "$done sur $total",
                    color = TeddTokens.Ink,
                    style = TeddType.MonoSm,
                )
            }
            if (health.overdue > 0 || health.stalled) {
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalAlignment = Alignment.CenterVertically) {
                    if (health.overdue > 0) org.teddapp.project.ui.TeddTag("${health.overdue} en retard", tone = org.teddapp.project.ui.TeddTagTone.Warning, icon = "alert")
                    if (health.stalled) org.teddapp.project.ui.TeddTag("Rien n'a bougé depuis ${health.stalest} j", icon = "clock")
                }
            }
        }
        health.nextAction?.let { task ->
            val late = !task.due_date.isNullOrBlank() && task.due_date!! < TeddDates.todayIso
            Box(Modifier.fillMaxWidth().height(1.dp).background(TeddTokens.Line))
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) { onOpenTask(task.id) }
                    .padding(14.dp),
                verticalArrangement = Arrangement.spacedBy(6.dp),
            ) {
                Text("À FAIRE ENSUITE", color = TeddTokens.InkMuted, style = TeddType.Overline)
                Text(task.title, color = TeddTokens.Ink, style = TeddType.BodyStrong, maxLines = 2)
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    task.due_date?.takeIf { it.isNotBlank() }?.let { due ->
                        org.teddapp.project.ui.TeddTag(
                            text = (TeddDates.formatRelative(due) ?: due) + if (late) " · en retard" else "",
                            tone = if (late) org.teddapp.project.ui.TeddTagTone.Warning else org.teddapp.project.ui.TeddTagTone.Neutral,
                            icon = "calendar",
                        )
                    }
                    task.estimated_minutes?.let { Text("$it min", color = TeddTokens.InkMuted, style = TeddType.MonoSm.copy(fontSize = 11.sp)) }
                    Spacer(modifier = Modifier.weight(1f))
                    if (onSendToFocus != null) {
                        org.teddapp.project.ui.TeddButton(
                            text = "Faire maintenant",
                            onClick = { onSendToFocus(task) },
                            kind = org.teddapp.project.ui.TeddButtonKind.Primary,
                            size = org.teddapp.project.ui.TeddButtonSize.Sm,
                            icon = "focus",
                        )
                    }
                }
            }
        }
    }
}

/**
 * Ce qu'on avait prévu, et ce qu'on a mis. « 20 min » tant qu'aucune course n'a eu
 * lieu, « 20 → 45 min » ensuite.
 *
 * Le temps réel s'accumule à chaque course de focus sur la tâche. C'est la matière
 * première pour savoir de combien on se trompe en estimant — une information qu'on
 * ne peut pas deviner, seulement mesurer.
 */
internal fun estimateLabel(estimated: Long?, real: Long?): String? {
    val done = real?.takeIf { it > 0 }
    return when {
        estimated != null && done != null -> "$estimated → $done min"
        estimated != null -> "$estimated min"
        done != null -> "$done min faites"
        else -> null
    }
}
